// ASU-branded confetti engine for celebration overlays (reusable).
//
// Ported verbatim (behaviour-wise) from asu-celebration-overlay.html — the
// approved confetti design the team signed off on. Renders on two canvases:
//   bgCanvas — blurred "far" glow layer
//   fgCanvas — sharp "near" particle layer
// Particles include real ASU pitchfork sprites, ribbons, circles, stars,
// streamers and sparkles. The shower keeps running until `stop()` is called,
// after which the remaining particles fall out of frame before the RAF halts.

import { CONFETTI_FORK_SRC } from './confettiAssets';

const MAROON = '#8C1D40';
const GOLD = '#FFC627';
const GOLD_SOFT = '#FFD45E';
const WHITE = '#FFFFFF';
const COLORS = [MAROON, GOLD, GOLD, GOLD_SOFT, WHITE];
const TAU = Math.PI * 2;

const NEAR_TARGET = 190;
const FAR_TARGET = 85;

const isGold = (c) => c === GOLD || c === GOLD_SOFT;

/**
 * Create a confetti controller bound to a pair of canvases.
 * @param {HTMLCanvasElement} bgCanvas blurred far layer
 * @param {HTMLCanvasElement} fgCanvas sharp near layer
 * @returns {{ start: () => void, stop: () => void, destroy: () => void }}
 */
export function createConfetti(bgCanvas, fgCanvas) {
  const fg = fgCanvas;
  const bg = bgCanvas;
  const fgx = fg.getContext('2d');
  const bgx = bg.getContext('2d');

  const FORKS = CONFETTI_FORK_SRC.map((src) => {
    const im = new Image();
    im.src = src;
    return im;
  });

  let W = 0;
  let H = 0;
  const DPR = Math.min(window.devicePixelRatio || 1, 2);
  let near = [];
  let far = [];
  let running = false;
  let rafId = null;

  function resize() {
    W = window.innerWidth;
    H = window.innerHeight;
    for (const c of [fg, bg]) {
      c.width = W * DPR;
      c.height = H * DPR;
    }
    fgx.setTransform(DPR, 0, 0, DPR, 0, 0);
    bgx.setTransform(DPR, 0, 0, DPR, 0, 0);
  }

  // 0 ribbon, 1 circle, 3 star, 4 streamer, 5 sparkle, 6 real ASU pitchfork image
  function pickShape() {
    const r = Math.random();
    if (r < 0.24) return 6;
    if (r < 0.31) return 3;
    if (r < 0.38) return 4;
    if (r < 0.45) return 5;
    if (r < 0.7) return 1;
    return 0;
  }

  function makeParticle(opts) {
    opts = opts || {};
    const shape = opts.shape != null ? opts.shape : pickShape();
    const size = 6 + Math.random() * 9;
    return {
      x: opts.x != null ? opts.x : Math.random() * W,
      y: opts.y != null ? opts.y : Math.random() * -H,
      w: size,
      h: shape === 4 ? size * (3 + Math.random() * 3) : size * (0.8 + Math.random() * 0.8),
      color: opts.color || COLORS[(Math.random() * COLORS.length) | 0],
      shape,
      img: shape === 6 ? FORKS[(Math.random() * FORKS.length) | 0] : null,
      vy: opts.vy != null ? opts.vy : 0.7 + Math.random() * 1.7,
      vx: opts.vx != null ? opts.vx : -0.7 + Math.random() * 1.4,
      rot: Math.random() * TAU,
      vr: -0.11 + Math.random() * 0.22,
      tilt: Math.random() * TAU,
      tiltSpeed: 0.025 + Math.random() * 0.05,
      sway: Math.random() * TAU,
      swaySpeed: 0.008 + Math.random() * 0.03,
      swayAmp: 0.5 + Math.random() * 1.2,
      twinkle: Math.random() * TAU,
      gravity: 0.008 + Math.random() * 0.014,
      life: 0
    };
  }

  function topUp() {
    while (near.length < NEAR_TARGET) near.push(makeParticle());
    while (far.length < FAR_TARGET) {
      const p = makeParticle();
      p.vy *= 0.55;
      p.vx *= 0.55;
      p.w *= 0.7;
      p.h *= 0.7;
      far.push(p);
    }
  }

  function burst() {
    const shots = [
      { x: 0, dir: 1 },
      { x: W, dir: -1 }
    ];
    for (const s of shots) {
      for (let i = 0; i < 80; i++) {
        const angle = -Math.PI / 2 + s.dir * (Math.random() * 0.7);
        const speed = 6 + Math.random() * 7;
        near.push(
          makeParticle({
            x: s.x,
            y: H + 10,
            vx: Math.cos(angle) * speed * s.dir * -1,
            vy: Math.sin(angle) * speed
          })
        );
      }
    }
  }

  function drawStar(ctx, r, color) {
    ctx.fillStyle = color;
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = (i * 4 * Math.PI) / 5 - Math.PI / 2;
      const x = Math.cos(a) * r;
      const y = Math.sin(a) * r;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  }

  function drawPitchfork(ctx, s, color) {
    ctx.beginPath();
    ctx.lineWidth = Math.max(1.4, s * 0.16);
    ctx.strokeStyle = color;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.moveTo(0, -s);
    ctx.lineTo(0, s);
    ctx.moveTo(-s * 0.7, -s * 0.2);
    ctx.lineTo(-s * 0.7, -s * 1.1);
    ctx.moveTo(0, -s * 0.2);
    ctx.lineTo(0, -s * 1.25);
    ctx.moveTo(s * 0.7, -s * 0.2);
    ctx.lineTo(s * 0.7, -s * 1.1);
    ctx.moveTo(-s * 0.7, -s * 0.2);
    ctx.lineTo(s * 0.7, -s * 0.2);
    ctx.stroke();
  }

  function drawSparkle(ctx, r, color, t) {
    ctx.globalAlpha = 0.4 + 0.6 * Math.abs(Math.sin(t));
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, r * 0.25);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-r, 0);
    ctx.lineTo(r, 0);
    ctx.moveTo(0, -r);
    ctx.lineTo(0, r);
    const d = r * 0.5;
    ctx.moveTo(-d, -d);
    ctx.lineTo(d, d);
    ctx.moveTo(-d, d);
    ctx.lineTo(d, -d);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  function step(ctx, list, isFar) {
    for (let i = list.length - 1; i >= 0; i--) {
      const p = list[i];
      p.life++;
      p.sway += p.swaySpeed;
      p.tilt += p.tiltSpeed;
      p.rot += p.vr;
      p.twinkle += 0.15;
      p.vy += p.gravity;
      p.vx *= 0.993;
      p.y += p.vy;
      p.x += p.vx + Math.sin(p.sway) * p.swayAmp;

      const flutter = Math.cos(p.tilt);

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      if (!isFar && isGold(p.color)) {
        ctx.shadowColor = 'rgba(255,198,39,0.7)';
        ctx.shadowBlur = 8;
      }

      if (p.shape === 6) {
        const fk = p.img;
        if (fk && fk.complete && fk.naturalWidth) {
          ctx.shadowBlur = 0;
          const ih = p.w * 2.4;
          const iw = ih * (fk.naturalWidth / fk.naturalHeight);
          ctx.scale(1, flutter || 0.05);
          ctx.drawImage(fk, -iw / 2, -ih / 2, iw, ih);
        } else {
          ctx.scale(1, flutter || 0.05);
          drawPitchfork(ctx, p.w * 0.85, p.color);
        }
      } else if (p.shape === 3) {
        ctx.scale(1, flutter || 0.05);
        drawStar(ctx, p.w * 0.7, p.color);
      } else if (p.shape === 1) {
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(0, 0, p.w * 0.5, 0, TAU);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 0.5;
        ctx.fillStyle = WHITE;
        ctx.beginPath();
        ctx.arc(-p.w * 0.15, -p.w * 0.15, p.w * 0.16, 0, TAU);
        ctx.fill();
        ctx.globalAlpha = 1;
      } else if (p.shape === 4) {
        ctx.fillStyle = p.color;
        const ww = p.w * 0.6;
        ctx.scale(1, flutter || 0.05);
        ctx.beginPath();
        ctx.moveTo(-ww / 2, -p.h / 2);
        ctx.quadraticCurveTo(ww, 0, -ww / 2, p.h / 2);
        ctx.quadraticCurveTo(ww * 0.2, 0, -ww / 2, -p.h / 2);
        ctx.fill();
      } else if (p.shape === 5) {
        drawSparkle(ctx, p.w * 0.7, p.color, p.twinkle);
      } else {
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, (-p.h / 2) * flutter, p.w, p.h * Math.abs(flutter) + 1);
      }
      ctx.restore();

      if (p.y - p.h > H + 30 || p.x < -60 || p.x > W + 60) {
        if (running && !isFar) Object.assign(p, makeParticle({ y: -20 }));
        else if (running && isFar) {
          const np = makeParticle({ y: -20 });
          np.vy *= 0.55;
          np.vx *= 0.55;
          np.w *= 0.7;
          np.h *= 0.7;
          list[i] = np;
        } else list.splice(i, 1);
      }
    }
  }

  function frame() {
    fgx.clearRect(0, 0, W, H);
    bgx.clearRect(0, 0, W, H);
    step(bgx, far, true);
    step(fgx, near, false);
    if (running) topUp();
    if (running || near.length || far.length) rafId = requestAnimationFrame(frame);
    else rafId = null;
  }

  function start() {
    resize();
    near = [];
    far = [];
    running = true;
    topUp();
    burst();
    if (!rafId) rafId = requestAnimationFrame(frame);
  }

  function stop() {
    running = false;
    // let existing particles fall out of frame, then the RAF loop halts itself
    if (!rafId) rafId = requestAnimationFrame(frame);
  }

  function destroy() {
    running = false;
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    near = [];
    far = [];
    window.removeEventListener('resize', resize);
  }

  window.addEventListener('resize', resize);

  return { start, stop, destroy };
}
